import { MetricSummary, PerformanceTest } from 'interfaces/performanceTest';
import {
  createTable,
  useTableInstance,
  getCoreRowModel,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { get } from 'lodash';
import CompareReportForm from '../CompareReportForm';
import { BiLinkExternal } from 'react-icons/bi';
import Loading from '../charts/Loading';

interface Props {
  performanceTest: PerformanceTest;
}

interface TableRow {
  label: string;
  metrics: MetricSummary;
  compareMetrics?: MetricSummary;
}

const table = createTable().setRowType<TableRow>();

const renderCompareValue = (
  row: TableRow,
  metric: string,
  provider: (metrics: MetricSummary) => number = null
) => {
  if (row.compareMetrics) {
    const getVal = provider ? provider : (m: MetricSummary) => get(m, metric);
    const diffValue =
      Math.round((getVal(row.metrics) / getVal(row.compareMetrics)) * 100) -
      100;
    const className =
      diffValue < 0 ? 'text-summer-green-500' : 'text-apricot-500';

    return <span className={className}>{`${diffValue || 0}%`}</span>;
  }
};

const calculateErrorRate = (metrics: MetricSummary): number => {
  return Math.round(metrics.totalFailures / metrics.totalRequests);
};

const CellWithCompare = ({ value, compareComponent, unit = null }) => {
  return (
    <span className="flex justify-between">
      <span>
        {value}
        {unit}
      </span>
      {compareComponent}
    </span>
  );
};

const defaultColumns = [
  table.createDataColumn('label', {
    cell: function labelCell(info) {
      return (
        <div
          style={{
            maxWidth: '400px',
          }}
          className="font-semibold overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {info.getValue()}
        </div>
      );
    },
    header: 'Label',
  }),
  table.createDataColumn((row) => row.metrics.totalRequests, {
    id: 'totalRequests',
    header: 'Request count',
    cell: function totalRequestsCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            'totalRequests'
          )}
        />
      );
    },
  }),
  table.createDataColumn((row) => row.metrics.latencies.avgMs, {
    id: 'avgResponseTime',
    header: 'Avg. latency',
    cell: function avgResponseCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            'latencies.avgMs'
          )}
          unit="ms"
        />
      );
    },
  }),
  table.createDataColumn((row) => row.metrics.latencies.p75Ms, {
    id: 'p75ResponseTime',
    header: 'p75 latency',
    cell: function p75ResponseCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            'latencies.p75Ms'
          )}
          unit="ms"
        />
      );
    },
  }),
  table.createDataColumn((row) => row.metrics.latencies.p90Ms, {
    id: 'p90ResponseTime',
    header: 'p90 latency',
    cell: function p90ResponseCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            'latencies.p90Ms'
          )}
          unit="ms"
        />
      );
    },
  }),
  table.createDataColumn((row) => row.metrics.latencies.p99Ms, {
    id: 'p99ResponseTime',
    header: 'p99 latency',
    cell: function p99ResponseCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            'latencies.p99Ms'
          )}
          unit="ms"
        />
      );
    },
  }),
  table.createDataColumn((row) => row.metrics.latencies.minMs, {
    id: 'minResponseTime',
    header: 'Min latency',
    cell: function minResponseCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            'latencies.minMs'
          )}
          unit="ms"
        />
      );
    },
  }),
  table.createDataColumn((row) => row.metrics.latencies.maxMs, {
    id: 'maxResponseTime',
    header: 'Max latency',
    cell: function minResponseCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            'latencies.maxMs'
          )}
          unit="ms"
        />
      );
    },
  }),
  table.createDataColumn((row) => calculateErrorRate(row.metrics), {
    id: 'errorRate',
    header: 'Error rate',
    cell: function minResponseCell(info) {
      return (
        <CellWithCompare
          value={info.getValue()}
          compareComponent={renderCompareValue(
            info.row.original,
            null,
            calculateErrorRate
          )}
          unit="%"
        />
      );
    },
  }),
];

const formatTableRows = (
  performanceTest: PerformanceTest,
  compareReport: PerformanceTest = null
): TableRow[] => {
  const rows = [];
  if (!performanceTest) {
    return rows;
  }

  rows.push({
    label: 'ALL',
    metrics: performanceTest.metrics,
    compareMetrics: compareReport ? compareReport.metrics : null,
  });

  Object.keys(performanceTest.metricsByLabel).forEach((label) => {
    rows.push({
      label,
      metrics: performanceTest.metricsByLabel[label],
      compareMetrics: compareReport
        ? compareReport.metricsByLabel[label]
        : null,
    });
  });

  return rows;
};

const ReportSummaryStats: React.FC<Props> = ({ performanceTest }) => {
  const [data, setData] = useState<TableRow[]>(
    formatTableRows(performanceTest)
  );
  const instance = useTableInstance(table, {
    data,
    columns: defaultColumns,
    getCoreRowModel: getCoreRowModel(),
  });
  const [compareReport, setCompareReport] = useState<PerformanceTest>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (performanceTest) {
      setData(formatTableRows(performanceTest));
      setLoading(false);
    }
  }, [performanceTest]);

  useEffect(() => {
    if (performanceTest && compareReport) {
      // TODO(bobsin): notify when labels are mismatched
      setData(formatTableRows(performanceTest, compareReport));
      setLoading(false);
    }
  }, [performanceTest, compareReport]);

  return (
    <>
      <header
        className="pl-3 border-b-2 border-gray-100 md:flex md:justify-between"
        style={{ lineHeight: '3rem' }}
      >
        <span className="flex flex-wrap">
          <span>Summary Stats</span>
          {compareReport ? (
            <a
              href={`/reports/${compareReport.id}?compareReportUuid=${performanceTest.id}`}
              target="_blank"
              className="text-san-juan-blue-500 border border-san-juan-blue-600 border-dashed rounded px-3 md:ml-8 mb-2 font-normal"
              rel="noreferrer"
            >
              Comparing with report {compareReport.label}.{' '}
              <BiLinkExternal className="inline-block" size="1.75rem" />
            </a>
          ) : null}
        </span>
        <CompareReportForm onCompare={setCompareReport} />
      </header>
      {loading ? (
        <Loading />
      ) : (
        <table className="min-w-full">
          <thead>
            {instance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b-2 border-gray-100">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="text-gray-700 font-semibold text-left py-2 px-2"
                  >
                    {header.isPlaceholder ? null : header.renderHeader()}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {instance.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="py-2 px-2 text-gray-700 font-normal"
                  >
                    {cell.renderCell()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

export default ReportSummaryStats;
